import { Injectable } from '@angular/core';

import { AuthService } from './auth.service';

const STORAGE_KEY = 'al-suppressedDialogs-v1';

/**
 * Remembers which confirmation dialogs a user ticked "don't show again" on.
 *
 * Keyed per user id, not globally: shared workstations are normal in schools
 * and universities, and one operator's decision must not silence a warning
 * for the next person who logs in. localStorage, following the same pattern
 * as AdoptionService and the widget's onboarding tour — the suppression is a
 * convenience, so a cleared browser simply shows the dialog again, which is
 * the safe direction.
 *
 * Never suppress anything destructive or irreversible with this. It exists
 * for dialogs that inform about a consequence (cost, duration) before an
 * action the operator will repeat many times.
 */
@Injectable({ providedIn: 'root' })
export class DialogSuppressionService {
  constructor(private auth: AuthService) {}

  private userKey(): string {
    return this.auth.accessTokenPayload$.value?.sub || 'anonymous';
  }

  private read(): Record<string, string[]> {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as Record<string, string[]>) : {};
    } catch {
      return {};
    }
  }

  isSuppressed(dialogKey: string): boolean {
    return (this.read()[this.userKey()] || []).includes(dialogKey);
  }

  suppress(dialogKey: string): void {
    try {
      const all = this.read();
      const user = this.userKey();
      const list = new Set(all[user] || []);
      list.add(dialogKey);
      all[user] = [...list];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
    } catch {
      /* a browser that refuses storage simply keeps showing the dialog */
    }
  }
}
